import React, { useState, useEffect } from 'react';
import { Layout, Avatar, Typography, Button, message } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import JoinTeamModal from './JoinTeamModal';
import CreateTeamModal from './CreateTeamModal';
import { LimsNavIcon } from './LimsNavIcon';
import { resolveUserAvatarSrc } from '../utils/resolveUploadAssetUrl';
import './TopNavbar.css';

const { Header } = Layout;
const { Text } = Typography;

interface TopNavbarProps {
  isCollapsed: boolean;
  onToggleSidebar?: () => void;
}

const TopNavbar: React.FC<TopNavbarProps> = ({ isCollapsed: _sidebarCollapsed, onToggleSidebar }) => {
  void _sidebarCollapsed;
  const { logout, username, role, realName, avatar, teamDisplayName, isDemoBrowseOnly } = useAuth();
  const navigate = useNavigate();
  const avatarSrc = resolveUserAvatarSrc(avatar);
  const [isMobile, setIsMobile] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const teamLabelButton = (compact: boolean) => (
    <Button
      type="text"
      className="lims-team-switcher"
      style={{ maxWidth: compact ? 48 : 240 }}
      onClick={() => navigate('/team')}
    >
      <LimsNavIcon name="hub" sizePx={20} className="lims-team-switcher-icon" />
      {!compact && (
        <span className="lims-team-switcher-name">{teamDisplayName || '未选择'}</span>
      )}
    </Button>
  );

  const submitGlobalSearch = () => {
    const q = globalSearch.trim();
    if (!q) {
      message.info('请输入关键词');
      return;
    }
    navigate(`/knowledge-base?q=${encodeURIComponent(q)}`);
  };

  return (
    <Header className="lims-top-navbar top-navbar">
      <div className="lims-top-inner">
        <div className="lims-top-left">
          {isMobile && (
            <Button
              type="text"
              className="lims-mobile-menu"
              icon={<LimsNavIcon name="menu" sizePx={22} />}
              onClick={onToggleSidebar}
            />
          )}
          {teamDisplayName &&
            (isDemoBrowseOnly ? (
              <div className="lims-demo-team-cluster">
                {teamLabelButton(isMobile)}
                <Button
                  size={isMobile ? 'small' : 'middle'}
                  className="lims-demo-team-join"
                  icon={<LimsNavIcon name="login" sizePx={isMobile ? 15 : 16} />}
                  onClick={() => setJoinOpen(true)}
                >
                  {isMobile ? '加入' : '加入团队'}
                </Button>
                <Button
                  type="primary"
                  size={isMobile ? 'small' : 'middle'}
                  className="lims-demo-team-create"
                  icon={<LimsNavIcon name="add_circle" sizePx={isMobile ? 15 : 16} />}
                  onClick={() => setCreateOpen(true)}
                >
                  {isMobile ? '创建' : '创建团队'}
                </Button>
              </div>
            ) : (
              teamLabelButton(isMobile)
            ))}
        </div>

        {!isMobile && (
          <div className="lims-top-center">
            <div className="lims-global-search">
              <LimsNavIcon name="search" sizePx={18} className="lims-global-search-icon" />
              <input
                type="search"
                className="lims-global-search-input"
                placeholder="全局搜索知识库..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitGlobalSearch()}
                aria-label="全局搜索"
              />
            </div>
          </div>
        )}

        <div className="lims-top-right">
          <NotificationBell />
          <button
            type="button"
            className="lims-icon-btn"
            aria-label="个人设置"
            onClick={() => navigate('/profile')}
          >
            <LimsNavIcon name="settings" sizePx={22} />
          </button>

          {!isMobile ? (
            <div className="lims-user-block">
              <Avatar
                size={32}
                src={avatarSrc}
                icon={<LimsNavIcon name="account_circle" sizePx={22} />}
                style={{
                  backgroundColor: avatar ? 'transparent' : 'rgba(0, 53, 95, 0.1)',
                  color: avatar ? undefined : 'var(--lims-primary)'
                }}
              />
              <div className="lims-user-meta">
                <Text strong style={{ fontSize: 14, color: 'var(--lims-on)' }}>
                  {realName || username || '用户'}
                </Text>
                <Text style={{ fontSize: 12, color: 'var(--lims-on-variant)' }}>{role || '—'}</Text>
              </div>
            </div>
          ) : (
            <Avatar
              size={32}
              src={avatarSrc}
              icon={<LimsNavIcon name="account_circle" sizePx={22} />}
              style={{ backgroundColor: avatar ? 'transparent' : 'rgba(0, 53, 95, 0.1)' }}
            />
          )}

          <Button
            type="text"
            className="lims-logout"
            icon={<LimsNavIcon name="logout" sizePx={20} />}
            onClick={handleLogout}
          >
            {!isMobile && '退出'}
          </Button>
        </div>
      </div>

      <JoinTeamModal open={joinOpen} onClose={() => setJoinOpen(false)} />
      <CreateTeamModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </Header>
  );
};

export default TopNavbar;
